import { useState, useMemo } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { motion } from "framer-motion";
import { Download, Users, Trash2, TrendingUp, ArrowUpDown } from "lucide-react";
import { fetcher } from "@/lib/api";
import "leaflet/dist/leaflet.css";
import "@/i18n/fase9";

const BAND_COLOR = {
  A: "#10B981", // emerald · óptimo
  B: "#84CC16", // lime · aceptable
  C: "#F59E0B", // amber · mejorable
  D: "#E76F51", // coral · bajo
  "n/d": "#9CA3AF", // grey · sin datos
};

function radiusForPop(pop, maxPop) {
  if (!pop || !maxPop) return 6;
  // sqrt scale · 8-32 px
  return 8 + Math.sqrt(pop / maxPop) * 24;
}

export default function ImpactoCobertura() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data } = useSWR("/public/impacto/coverage", fetcher);
  const [sortKey, setSortKey] = useState("population");
  const [sortDir, setSortDir] = useState("desc");

  const rows = data?.by_concello || [];
  const totals = data?.totals || {};
  const maxPop = useMemo(
    () => Math.max(1, ...rows.map((r) => r.population || 0)),
    [rows]
  );

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "string") {
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const bandCounts = useMemo(() => {
    const c = { A: 0, B: 0, C: 0, D: 0, "n/d": 0 };
    rows.forEach((r) => {
      c[r.score_band] = (c[r.score_band] || 0) + 1;
    });
    return c;
  }, [rows]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "name_es" ? "asc" : "desc");
    }
  };

  const downloadCsv = () => {
    const header = [
      "code",
      "concello",
      "population",
      "col_points",
      "containers",
      "ratio_hab_per_container",
      "ratio_hab_per_col_point",
      "score_band",
    ];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      const name = (lang === "es" ? r.name_es : r.name_gl) || r.code;
      lines.push(
        [
          r.code,
          `"${name.replace(/"/g, '""')}"`,
          r.population ?? "",
          r.col_points ?? "",
          r.containers ?? "",
          r.ratio_hab_per_container ?? "",
          r.ratio_hab_per_col_point ?? "",
          r.score_band ?? "",
        ].join(",")
      );
    });
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "salnes-cobertura-territorial.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-10" data-testid="impacto-cobertura">
      {/* KPIs */}
      <div className="lg:col-span-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile
          icon={Users}
          label={t("fase9.cobertura.total_pop")}
          value={totals.population?.toLocaleString() || "—"}
          testid="cov-kpi-pop"
        />
        <Tile
          icon={Trash2}
          label={t("fase9.cobertura.total_containers")}
          value={totals.containers?.toLocaleString() || "—"}
          accent="text-salnes-emerald"
          testid="cov-kpi-containers"
        />
        <Tile
          icon={TrendingUp}
          label={t("fase9.cobertura.avg_ratio")}
          value={totals.ratio_hab_per_container ?? "—"}
          suffix="hab/cont"
          accent="text-salnes-coral"
          testid="cov-kpi-ratio"
        />
        <BandsTile counts={bandCounts} testid="cov-kpi-bands" />
      </div>

      {/* Map + Legend */}
      <div className="lg:col-span-7">
        <div className="rounded-2xl border hairline bg-white p-2 overflow-hidden">
          <div className="h-[420px] rounded-xl overflow-hidden" data-testid="cov-map-wrap">
            <MapContainer
              center={[42.55, -8.78]}
              zoom={11}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap · CARTO'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                maxZoom={19}
              />
              {rows
                .filter((r) => r.center && r.population)
                .map((r) => (
                  <CircleMarker
                    key={r.code}
                    center={[r.center[0], r.center[1]]}
                    radius={radiusForPop(r.population, maxPop)}
                    pathOptions={{
                      color: BAND_COLOR[r.score_band] || BAND_COLOR["n/d"],
                      fillColor: BAND_COLOR[r.score_band] || BAND_COLOR["n/d"],
                      fillOpacity: 0.55,
                      weight: 2,
                    }}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold text-salnes-atlantic">
                          {lang === "es" ? r.name_es : r.name_gl}
                        </div>
                        <div>
                          {t("fase9.cobertura.total_pop")}:{" "}
                          <b>{r.population.toLocaleString()}</b>
                        </div>
                        <div>
                          {t("fase9.cobertura.th_containers")}:{" "}
                          <b>{r.containers}</b>
                        </div>
                        <div>
                          {t("fase9.cobertura.th_ratio")}:{" "}
                          <b>{r.ratio_hab_per_container ?? "—"}</b>
                        </div>
                        <div>
                          {t("fase9.cobertura.th_band")}:{" "}
                          <span
                            className="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium"
                            style={{ background: BAND_COLOR[r.score_band] }}
                          >
                            {r.score_band}
                          </span>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
            </MapContainer>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 pt-3 pb-2 text-[10px] text-salnes-stone">
            <span>{t("fase9.cobertura.map_hint")}</span>
            <div className="flex items-center gap-3">
              {["A", "B", "C", "D", "n/d"].map((b) => (
                <span key={b} className="flex items-center gap-1">
                  <i
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ background: BAND_COLOR[b] }}
                  />
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table + CSV */}
      <div className="lg:col-span-5">
        <div className="rounded-2xl border hairline bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b hairline">
            <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
              {t("fase9.cobertura.section_title")}
            </div>
            <button
              onClick={downloadCsv}
              className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-salnes-atlantic text-white hover:bg-salnes-atlantic/90 transition"
              data-testid="cov-csv-download"
            >
              <Download size={12} />
              {t("fase9.cobertura.csv_download")}
            </button>
          </div>
          <div className="max-h-[380px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-salnes-sand/40 text-salnes-stone">
                <tr>
                  <Th onClick={() => toggleSort("name_es")}>
                    {t("fase9.cobertura.th_concello")}
                  </Th>
                  <Th onClick={() => toggleSort("population")} align="right">
                    {t("fase9.cobertura.th_population")}
                  </Th>
                  <Th onClick={() => toggleSort("containers")} align="right">
                    {t("fase9.cobertura.th_containers")}
                  </Th>
                  <Th
                    onClick={() => toggleSort("ratio_hab_per_container")}
                    align="right"
                  >
                    {t("fase9.cobertura.th_ratio")}
                  </Th>
                  <Th onClick={() => toggleSort("score_band")} align="center">
                    {t("fase9.cobertura.th_band")}
                  </Th>
                </tr>
              </thead>
              <tbody data-testid="cov-table-body">
                {sorted.map((r) => (
                  <tr
                    key={r.code}
                    className="border-t hairline hover:bg-salnes-sand/20"
                    data-testid={`cov-row-${r.code}`}
                  >
                    <td className="px-3 py-2 text-salnes-ink">
                      {lang === "es" ? r.name_es : r.name_gl}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.population?.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.containers?.toLocaleString() ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.ratio_hab_per_container ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium"
                        style={{ background: BAND_COLOR[r.score_band] }}
                      >
                        {r.score_band}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p
          className="text-[10px] text-salnes-stone italic mt-2"
          data-testid="cov-note-provisional"
        >
          {t("fase9.cobertura.note_provisional")}
        </p>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, suffix, accent = "text-salnes-atlantic", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl border hairline bg-white p-5 min-h-[110px] flex flex-col justify-between"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
        <Icon size={12} strokeWidth={1.6} className={accent} /> {label}
      </div>
      <div className="font-serif text-3xl text-salnes-ink leading-none tabular-nums flex items-baseline gap-2">
        {value}
        {suffix && (
          <span className="text-[10px] tracking-wider text-salnes-stone uppercase font-sans">
            {suffix}
          </span>
        )}
      </div>
    </motion.div>
  );
}

function BandsTile({ counts, testid }) {
  const { t } = useTranslation();
  const items = [
    { k: "A", label: t("fase9.cobertura.band_a") },
    { k: "B", label: t("fase9.cobertura.band_b") },
    { k: "C", label: t("fase9.cobertura.band_c") },
    { k: "D", label: t("fase9.cobertura.band_d") },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl border hairline bg-white p-5 min-h-[110px]"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
        Bandas MITECO
      </div>
      <div className="grid grid-cols-4 gap-2">
        {items.map((it) => (
          <div key={it.k} className="text-center">
            <div
              className="mx-auto w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white"
              style={{ background: BAND_COLOR[it.k] }}
            >
              {it.k}
            </div>
            <div className="text-[16px] font-serif mt-1 tabular-nums text-salnes-ink">
              {counts[it.k] || 0}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function Th({ children, onClick, align = "left" }) {
  return (
    <th
      onClick={onClick}
      className={`px-3 py-2 text-[10px] uppercase tracking-[0.16em] font-medium cursor-pointer select-none text-${align}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown size={9} className="opacity-40" />
      </span>
    </th>
  );
}
